import { logout } from "@/lib/logout.ts";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { getReauthUrl } from "../../components/connectors/api.ts";
import { AvatarWithFallback } from "../../components/ui/avatar.tsx";
import { Badge } from "../../components/ui/badge.tsx";
import { Button } from "../../components/ui/button.tsx";
import { useCurrentUserContext } from "../../hooks/useUser.tsx";
import { formatPhoneNumber } from "../../lib/phone.ts";
import { verifiedPhoneHandleValue } from "@/shared/phone.ts";
import { ChangePhoneNumberDialog } from "./ChangePhoneNumberDialog.tsx";
import { DeleteAccount } from "./DeleteAccount.tsx";
import { ImportFromAssistantCard } from "./ImportFromAssistantCard.tsx";
import { panelDescriptionClass, SettingsPanelHeader } from "./SettingsPanelHeader.tsx";
import { OtherAccounts } from "./OtherAccounts.tsx";
import { useStepUp } from "@/lib/useStepUp.ts";
import { useConnectorOAuthResults } from "@/components/connectors/useConnectorOAuthResults.ts";
import { resolveApiPath } from "@/lib/utils.ts";
import { connectorAddAccessPath, MISSING_ACCESS_LABELS } from "@/shared/connectors/reauth.ts";
import type { ConnectorSummary } from "@/types/connectors.ts";

/**
 * The Accounts settings panel: the active account's profile card (reconnect,
 * phone number, delete account) and the other connected accounts.
 */
export const AccountsPanel = () => {
  const { userContext } = useCurrentUserContext();

  const profileUser = userContext?.user.userContact;
  const activeAccount = userContext?.user.accounts?.find((a) => a.id === userContext?.user.id);
  const profileDisplayName = profileUser?.name || activeAccount?.displayName || "Unknown";
  const profileEmail =
    activeAccount?.displayName ?? userContext?.user.userPrimaryEmail ?? undefined;
  const profilePhoneNumber = verifiedPhoneHandleValue(profileUser?.handles);
  const loginConnector = userContext?.user.connectors?.find((c) => c.isLogin);

  return (
    <div className="w-full space-y-8 pb-12">
      <div>
        <SettingsPanelHeader title="Accounts" />
        <p className={panelDescriptionClass}>
          The accounts you've signed in with, and how you switch between them.
        </p>
        {/* Everything inside this card belongs to the currently active
            account; accounts you could switch to are listed after it. */}
        {profileUser && (
          <div className="rounded-2xl border bg-card">
            <div className="flex flex-row items-start gap-3 p-4">
              <AvatarWithFallback
                size="2xl"
                person={{
                  name: profileDisplayName,
                  value: profileEmail || profileDisplayName,
                  picture: profileUser?.picture,
                }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-xl font-semibold">{profileDisplayName}</div>
                {profileEmail && (
                  <div className="text-sm text-muted-foreground truncate">{profileEmail}</div>
                )}
              </div>
              {loginConnector?.needsAuth && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="shrink-0 self-center gap-1.5! text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-statsig-name="settings-account-login-reconnect"
                >
                  <a href={getReauthUrl(loginConnector)} target="_blank" rel="noopener noreferrer">
                    <AlertCircle className="size-4" strokeWidth={2.5} />
                    Reconnect
                  </a>
                </Button>
              )}
            </div>
            {loginConnector?.missingAccess?.length ? (
              <MissingAccess connector={loginConnector} />
            ) : null}
            {profilePhoneNumber && <PhoneNumberSection phoneNumber={profilePhoneNumber} />}
            <div className="border-t p-4">
              <h3 className="text-sm font-medium text-foreground mb-2">Delete account</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Permanently delete this account and all synced data.
              </p>
              <DeleteAccount />
            </div>
          </div>
        )}
        {profileUser && <OtherAccounts onLogout={() => void logout()} />}
      </div>
      <ImportFromAssistantCard />
    </div>
  );
};

/**
 * The account's verified number and the way to change it. An account whose
 * number was never proven by code proves it first, so Change goes to the
 * step-up instead of the dialog.
 */
const PhoneNumberSection = ({ phoneNumber }: { phoneNumber: string }) => {
  const stepUp = useStepUp();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <div className="border-t p-4">
      <h3 className="text-sm font-medium text-foreground mb-2">Phone number</h3>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm">{formatPhoneNumber(phoneNumber)}</span>
          <Badge variant="accent" size="sm">
            Receiving texts
          </Badge>
        </div>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="shrink-0"
          data-statsig-name="settings-account-change-phone-number"
        >
          {stepUp.required ? (
            <a href={stepUp.href}>Change</a>
          ) : (
            <button type="button" onClick={() => setDialogOpen(true)}>
              Change
            </button>
          )}
        </Button>
      </div>
      <ChangePhoneNumberDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentNumber={phoneNumber}
      />
    </div>
  );
};

/**
 * What the sign-in account cannot reach yet, each with a connect that adds it
 * to that same account. A sign-up that ends at the profile grant has none of
 * them, and nothing else in Settings says so.
 */
const MissingAccess = ({
  connector,
}: {
  connector: Pick<ConnectorSummary, "id" | "provider" | "missingAccess">;
}) => {
  const stepUp = useStepUp();
  // The connect tab closes itself, so a refusal (a box left unticked on
  // Google's screen) is only heard here.
  useConnectorOAuthResults();
  const missing = connector.missingAccess ?? [];

  return (
    <div className="border-t p-4 space-y-3">
      {missing.map((add) => (
        <div key={add} className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground">{MISSING_ACCESS_LABELS[add].row}</span>
          <Button
            asChild
            variant="outline"
            size="sm"
            className="shrink-0"
            data-statsig-name={`settings-account-connect-${add}`}
          >
            {/* Adding access is gated on a proven number, like any connect. */}
            {stepUp.required ? (
              <a href={stepUp.href}>{MISSING_ACCESS_LABELS[add].button}</a>
            ) : (
              <a
                href={resolveApiPath(connectorAddAccessPath(connector, add))}
                target="_blank"
                rel="noopener noreferrer"
              >
                {MISSING_ACCESS_LABELS[add].button}
              </a>
            )}
          </Button>
        </div>
      ))}
    </div>
  );
};
